import * as React from 'react'
import { createConsumer } from '@rails/actioncable'

import { cableUrl } from '../../utils/channels'
import { camelKeys, snakeKeys } from '../../utils/json'
import {
  ChannelState,
  CompletionsChannelPayload,
  CompletionsChannelPayloadType,
  CompletionDelta,
  Completion,
  PilotContextType,
  FunctionCallChannelPayload,
  FunctionCallChannelPayloadType,
  PilotFunctionCall,
  FunctionCallProgressMessage,
} from '../../types'
import {
  appendAssistantCompletionDelta,
  updateChannelState,
  upsertCompletion,
  addFunctionCall,
  updateFunctionCallProgress,
} from './slice'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { getCurrentThreadKey, getCurrentThread } from './selectors'
import PilotContext from 'bundles/Pilot/contexts/PilotContext'

const CHANNEL_NAME = 'PilotCompletionsChannel'

type Props = {
  children: React.ReactNode
}

const Channel = ({ children }: Props): React.ReactElement => {
  const dispatch = useAppDispatch()
  const { threadKey: contextThreadKey } = React.useContext<PilotContextType>(PilotContext)
  const currentThreadKey = useAppSelector(getCurrentThreadKey)
  const currentThread = useAppSelector(getCurrentThread)
  const completionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const threadKey = currentThreadKey || contextThreadKey

  const handlePayloadReceived = (
    payload: CompletionsChannelPayload | FunctionCallChannelPayload
  ) => {
    if (
      'type' in payload &&
      (payload.type === CompletionsChannelPayloadType.Delta ||
        payload.type === CompletionsChannelPayloadType.Object)
    ) {
      const completionPayload = payload

      switch (completionPayload.type) {
        case CompletionsChannelPayloadType.Delta: {
          const delta = completionPayload.data as CompletionDelta
          dispatch(appendAssistantCompletionDelta(delta))

          if (completionTimeoutRef.current) {
            clearTimeout(completionTimeoutRef.current)
          }

          completionTimeoutRef.current = setTimeout(() => {
            const streamingFinishedEvent = new CustomEvent('pilot:streaming:finished', {
              detail: { timestamp: Date.now() },
            })
            document.dispatchEvent(streamingFinishedEvent)
          }, 2000)

          break
        }
        case CompletionsChannelPayloadType.Object: {
          const completion = completionPayload.data as Completion
          dispatch(upsertCompletion(completion))

          if (completionTimeoutRef.current) {
            clearTimeout(completionTimeoutRef.current)
            completionTimeoutRef.current = null
          }

          const streamingFinishedEvent = new CustomEvent('pilot:streaming:finished', {
            detail: { timestamp: Date.now() },
          })
          document.dispatchEvent(streamingFinishedEvent)

          break
        }
      }
    }

    if (
      'type' in payload &&
      (payload.type === FunctionCallChannelPayloadType.FunctionCall ||
        payload.type === FunctionCallChannelPayloadType.Progress)
    ) {
      const functionPayload = payload
      switch (functionPayload.type) {
        case FunctionCallChannelPayloadType.FunctionCall:
          dispatch(addFunctionCall(functionPayload.data as PilotFunctionCall))
          break
        case FunctionCallChannelPayloadType.Progress:
          dispatch(updateFunctionCallProgress(functionPayload.data as FunctionCallProgressMessage))
          break
      }
    }
  }

  const createChannel = () => {
    if (!threadKey || !currentThread?.id) {
      dispatch(updateChannelState(ChannelState.Disconnected))
      return null
    }

    return createConsumer(cableUrl()).subscriptions.create(
      snakeKeys({
        channel: CHANNEL_NAME,
        threadKey,
        pilotThreadId: currentThread.id,
      }),
      {
        connected: handleConnected,
        disconnected: handleDisconnected,
        received: handleReceived,
      }
    )
  }

  const handleConnected = () => dispatch(updateChannelState(ChannelState.Connected))
  const handleDisconnected = () => dispatch(updateChannelState(ChannelState.Disconnected))
  const handleReceived = (payload: any) => handlePayloadReceived(camelKeys(payload))

  React.useEffect(() => {
    const channel = createChannel()
    if (channel) {
      return () => {
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current)
          completionTimeoutRef.current = null
        }
        channel.unsubscribe()
      }
    }

    return () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current)
        completionTimeoutRef.current = null
      }
    }
  }, [threadKey, currentThread?.id])

  return <>{children}</>
}

export default Channel
